import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  UserCheck,
  Briefcase,
  CheckCircle2,
  EyeIcon,
  XCircle,
  TrendingUp,
  CalendarDays,
  IndianRupee,
} from "lucide-react";
import { STATUS_META } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";

const STAT_CONFIG = [
  { key: "total", label: "Total Candidates", icon: Users, accent: "bg-brand-50 text-brand-700", to: "/candidates" },
  { key: "new", label: "New Submissions", icon: TrendingUp, accent: "bg-amber-50 text-amber-700", to: "/candidates?status=new" },
  { key: "seen", label: "Reviewed (Seen)", icon: EyeIcon, accent: "bg-slate-100 text-slate-700", to: "/candidates?status=seen" },
  { key: "shortlisted", label: "Shortlisted", icon: UserCheck, accent: "bg-blue-50 text-blue-700", to: "/candidates?status=shortlisted" },
  { key: "lined_up", label: "Lined Up", icon: Briefcase, accent: "bg-orange-50 text-orange-700", to: "/candidates?status=lined_up" },
  { key: "upcoming_interviews", label: "Upcoming Interviews", icon: CalendarDays, accent: "bg-violet-50 text-violet-700", to: "/calendar" },
  { key: "hired", label: "Hired", icon: CheckCircle2, accent: "bg-emerald-50 text-emerald-700", to: "/candidates?status=hired" },
  { key: "rejected", label: "Rejected", icon: XCircle, accent: "bg-rose-50 text-rose-700", to: "/candidates?status=rejected" },
];

// Admin-only extra tiles
const ADMIN_STAT_CONFIG = [
  {
    key: "paid_downloads",
    label: "Paid Resume Downloads",
    icon: IndianRupee,
    accent: "bg-emerald-50 text-emerald-700",
    to: "/candidates?paid=1",
  },
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const tiles =
    user?.role === "admin" ? [...STAT_CONFIG, ...ADMIN_STAT_CONFIG] : STAT_CONFIG;

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <AppShell
      title="Dashboard"
      subtitle="Overview of your candidate pipeline. Click any tile or category to view the list."
    >
      {!stats ? (
        <div className="text-ink-600 text-sm animate-pulse">Loading stats…</div>
      ) : (
        <>
          <div
            data-testid="dashboard-stats-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8"
          >
            {tiles.map(({ key, label, icon: Icon, accent, to }) => (
              <Link
                to={to}
                key={key}
                data-testid={`stat-${key}`}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-xl"
              >
                <Card className="tj-card tj-card-hover cursor-pointer">
                  <CardContent className="p-5">
                    <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
                      <Icon size={18} />
                    </div>
                    <div className="mt-4 tj-stat-num">{stats[key] ?? 0}</div>
                    <div className="mt-1 text-xs uppercase tracking-[0.18em] text-ink-600">
                      {label}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="tj-card lg:col-span-2">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-ink-900">
                    Candidates by Job Category
                  </h3>
                  <span className="text-[11px] text-ink-600">click to filter</span>
                </div>
                {stats.by_category?.length === 0 ? (
                  <p className="text-sm text-ink-600">No candidates yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="by-category-list">
                    {stats.by_category.map((c) => {
                      const max = Math.max(
                        ...stats.by_category.map((x) => x.count)
                      );
                      const pct = max ? (c.count / max) * 100 : 0;
                      return (
                        <button
                          key={c.category}
                          type="button"
                          data-testid={`category-${c.category}`}
                          onClick={() =>
                            nav(
                              `/candidates?job_category=${encodeURIComponent(c.category)}`
                            )
                          }
                          className="w-full text-left rounded-lg p-2 -mx-2 hover:bg-brand-50/60 focus:bg-brand-50 transition-colors group"
                        >
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-ink-800 group-hover:text-brand-800">
                              {c.category}
                            </span>
                            <span className="text-ink-600">{c.count}</span>
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-brand-600 rounded-full transition-all group-hover:bg-brand-700"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="tj-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold text-ink-900">
                    Recent Submissions
                  </h3>
                  <Link
                    to="/candidates"
                    data-testid="view-all-candidates"
                    className="text-xs font-medium text-brand-700 hover:underline"
                  >
                    View all →
                  </Link>
                </div>
                {stats.recent?.length === 0 ? (
                  <p className="text-sm text-ink-600">No recent submissions.</p>
                ) : (
                  <ul className="space-y-3">
                    {stats.recent.map((c) => {
                      const meta = STATUS_META[c.status] || STATUS_META.new;
                      return (
                        <li key={c.id}>
                          <Link
                            to={`/candidates/${c.id}`}
                            data-testid={`recent-${c.id}`}
                            className="flex items-center justify-between gap-3 p-2 -mx-2 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-ink-900 truncate">
                                {c.full_name}
                              </div>
                              <div className="text-xs text-ink-600 truncate">
                                {c.job_category}
                              </div>
                            </div>
                            <Badge className={`${meta.cls} border-0`}>
                              {meta.label}
                            </Badge>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </AppShell>
  );
}
